import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { errMsg, formatPrice } from '@/lib/digitalApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const STATUSES = ['all', 'paid', 'free', 'pending', 'revoked', 'refunded', 'failed'];
const STATUS_CLS = { paid: 'text-emerald-700', free: 'text-blue-700', pending: 'text-amber-700', revoked: 'text-red-700', refunded: 'text-red-700', failed: 'text-red-700' };
const ACTIONS = [
  ['resend', 'Resend Link', 'Email an additional new link (existing links keep working).'],
  ['reissue', 'Reissue Link', 'Replace all existing links with a fresh one and email it.'],
  ['reset', 'Reset Downloads', 'Reset the latest link to 0 downloads and 90 more days.'],
];

const OrderDrawer = ({ id, onClose, onChanged }) => {
  const [o, setO] = useState(null);
  const load = () => adminApi.get(`/admin/digital-products/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const act = async (action) => {
    if (action === 'revoke' && !window.confirm('Revoke all download access for this order?')) return;
    try { await adminApi.post(`/admin/digital-products/orders/${id}/${action}`); toast.success('Done'); await load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const entitled = o && ['paid', 'free'].includes(o.status);
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="dp-order-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#FAF8F5] h-full overflow-y-auto shadow-2xl" data-lenis-prevent>
        <div className="sticky top-0 bg-[#FAF8F5] border-b border-[#eae4d8] px-6 py-4 flex justify-between">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">Order</div>
          <button onClick={onClose} className="text-sm text-[#555]" data-testid="dp-order-drawer-close">Close ×</button>
        </div>
        {!o ? <div className="py-16 text-center text-[#888]">Loading…</div> : (
          <div className="px-6 py-6 space-y-6 text-sm">
            <div>
              <div className="text-2xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{o.product_title}</div>
              <div className="text-[#555]">{o.first_name} · {o.email}</div>
              <div className={`mt-1 text-xs uppercase tracking-[0.14em] ${STATUS_CLS[o.status]}`} data-testid="dp-order-status">{o.status}</div>
            </div>
            <div className="bg-white border border-[#eae4d8] rounded-xl p-4 grid grid-cols-2 gap-2">
              <div>Amount: {o.kind === 'free' ? 'Free' : formatPrice(o.amount_cents)}</div>
              <div>Downloads: {o.total_downloads || 0}</div>
              <div>Created: {fmt(o.created_at)}</div>
              <div>Paid: {fmt(o.paid_at)}</div>
              <div>Founder&rsquo;s List opt-in: {o.founders_list_opt_in ? `Yes (${o.founders_list_result || 'pending'})` : 'No'}</div>
              <div>Last email: {o.last_email_ok === false ? <span className="text-red-600">Failed</span> : fmt(o.last_email_sent_at)}</div>
              {o.stripe_session_id && <div className="col-span-2 text-xs text-[#888] break-all">Stripe session: {o.stripe_session_id}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              {entitled && ACTIONS.map(([a, l, hint]) => (
                <button key={a} title={hint} onClick={() => act(a)} className="text-xs uppercase tracking-[0.14em] px-4 py-2 border border-[#111] rounded-full" data-testid={`dp-order-action-${a}`}>{l}</button>
              ))}
              {entitled && <button onClick={() => act('revoke')} className="text-xs uppercase tracking-[0.14em] px-4 py-2 border border-red-700 text-red-700 rounded-full" data-testid="dp-order-action-revoke">Revoke Access</button>}
              {['revoked', 'refunded'].includes(o.status) && <button onClick={() => act('restore')} className="text-xs uppercase tracking-[0.14em] px-4 py-2 border border-[#111] rounded-full" data-testid="dp-order-action-restore">Restore Access</button>}
              {o.status === 'pending' && <button onClick={() => act('check-payment')} className="text-xs uppercase tracking-[0.14em] px-4 py-2 border border-[#111] rounded-full" data-testid="dp-order-action-check-payment">Check Payment with Stripe</button>}
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">Download links</div>
              <div className="bg-white border border-[#eae4d8] rounded-xl divide-y divide-[#eae4d8]">
                {o.links.length === 0 && <div className="p-4 text-[#888]">No links issued.</div>}
                {o.links.map((l) => (
                  <div key={l.created_at} className="p-4 flex justify-between gap-3" data-testid="dp-order-link-row">
                    <div><div className="text-[#111]">{l.code} <span className="text-xs text-[#888]">({l.reason})</span></div><div className="text-xs text-[#888]">Issued {fmt(l.created_at)} · expires {fmt(l.expires_at)}</div></div>
                    <div className="text-right"><div className="text-xs uppercase tracking-[0.12em]">{l.state.replace('_', ' ')}</div><div className="text-xs text-[#888]">{l.download_count}/{l.max_downloads}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function OrdersPanel({ products }) {
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], stats: {}, revenue_cents: 0 });
  const [openId, setOpenId] = useState(null);
  const load = () => adminApi.get('/admin/digital-products/orders', { params: { status, q: q || undefined } }).then((r) => setData(r.data)).catch(() => toast.error('Failed to load orders'));
  const exportCsv = async () => {
    try {
      const r = await adminApi.get('/admin/digital-products/orders/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `digital-orders-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [status, q]);

  return (
    <div data-testid="dp-orders-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`dp-orders-filter-${s}`}
            className={`text-xs tracking-[0.14em] uppercase px-4 py-1.5 rounded-full border ${status === s ? 'bg-[#111] text-[#FAF8F5] border-[#111]' : 'bg-white text-[#555] border-[#eae4d8]'}`}>
            {s}{s !== 'all' && data.stats[s] ? ` · ${data.stats[s]}` : ''}
          </button>
        ))}
        <span className="text-xs text-[#555] ml-2" data-testid="dp-orders-revenue">Revenue: {formatPrice(data.revenue_cents)}</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, product" className="ml-auto px-4 py-2 border border-[#eae4d8] rounded-full text-sm" data-testid="dp-orders-search" />
        <button type="button" onClick={exportCsv} className="text-xs tracking-[0.18em] uppercase px-5 py-2 border border-[#111] rounded-full hover:bg-[#111] hover:text-[#FAF8F5] transition-colors" data-testid="dp-orders-export-csv">Export CSV</button>
      </div>
      <div className="border border-[#eae4d8] rounded-xl overflow-x-auto bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FAF8F5] text-[#888] text-[11px] tracking-[0.14em] uppercase">
            <tr>{['Date', 'Customer', 'Product', 'Amount', 'Status', 'Downloads', 'List'].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#888]" data-testid="dp-orders-empty">No orders yet.</td></tr>}
            {data.items.map((o) => (
              <tr key={o.id} onClick={() => setOpenId(o.id)} className="border-t border-[#eae4d8] hover:bg-[#FAF8F5] cursor-pointer" data-testid={`dp-order-row-${o.id}`}>
                <td className="px-4 py-3 text-[#555]">{fmt(o.created_at)}</td>
                <td className="px-4 py-3"><div className="text-[#111]">{o.first_name}</div><div className="text-xs text-[#888]">{o.email}</div></td>
                <td className="px-4 py-3 text-[#111]">{o.product_title || products.find((p) => p.id === o.product_id)?.title}</td>
                <td className="px-4 py-3">{o.kind === 'free' ? 'Free' : formatPrice(o.amount_cents)}</td>
                <td className={`px-4 py-3 text-xs uppercase tracking-[0.12em] ${STATUS_CLS[o.status]}`}>{o.status}</td>
                <td className="px-4 py-3">{o.total_downloads || 0}</td>
                <td className="px-4 py-3 text-xs">{o.founders_list_opt_in ? 'Opted in' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openId && <OrderDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
